import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type {
	LoginModel,
	AuthenticateResponse,
	ChangePasswordModel,
	ResetPasswordModel,
	ApplicationUserDto,
} from '@/lib/models/auth';

/**
 * Interfaccia per gli endpoints di autenticazione
 */
export interface AuthEndpoints {
	/**
	 * Ping autenticato per verificare se l'utente è ancora loggato
	 * @returns Promise con FetchResponse contenente void
	 */
	authenticatedPing(): Promise<FetchResponse<void>>;

	/**
	 * Login utente con username e password
	 * @param loginData - Dati di login (username e password)
	 * @returns Promise con FetchResponse contenente AuthenticateResponse
	 */
	login(loginData: LoginModel): Promise<FetchResponse<AuthenticateResponse>>;

	/**
	 * Refresh del token di autenticazione
	 * @param token - Token da rinnovare
	 * @returns Promise con FetchResponse contenente AuthenticateResponse
	 */
	refreshToken(token: string): Promise<FetchResponse<AuthenticateResponse>>;

	/**
	 * Cambio password utente
	 * @param passwordData - Dati per il cambio password (vecchia e nuova password)
	 * @returns Promise con FetchResponse contenente void
	 */
	changePassword(passwordData: ChangePasswordModel): Promise<FetchResponse<void>>;

	/**
	 * Invia email per il reset della password
	 * @param username - Username per cui inviare il reset password
	 * @returns Promise con FetchResponse contenente void
	 */
	sendResetPassword(username: string): Promise<FetchResponse<void>>;

	/**
	 * Reset della password utente con token
	 * @param resetData - Dati per il reset password (username, token, nuova password)
	 * @returns Promise con FetchResponse contenente void
	 */
	resetPassword(resetData: ResetPasswordModel): Promise<FetchResponse<void>>;

	/**
	 * Recupera lo username di un utente per ID
	 * @param userId - ID dell'utente
	 * @returns Promise con FetchResponse contenente string (username)
	 */
	getUserName(userId: string): Promise<FetchResponse<string>>;

	/**
	 * Recupera le informazioni dell'utente corrente autenticato
	 * @returns Promise con FetchResponse contenente ApplicationUserDto
	 */
	getUserInfo(): Promise<FetchResponse<ApplicationUserDto>>;

	/**
	 * Recupera le informazioni di un utente impersonato tramite workerId
	 * @param workerId - ID del lavoratore da impersonare
	 * @returns Promise con FetchResponse contenente ApplicationUserDto
	 */
	getImpersonatedUserInfo(workerId: string): Promise<FetchResponse<ApplicationUserDto>>;
}

/**
 * Implementazione degli endpoints di autenticazione
 * Mantiene compatibilità con il JavaScript originale
 */
const authEndpoints: AuthEndpoints = {
	authenticatedPing(): Promise<FetchResponse<void>> {
		return api.get('/authPing');
	},

	login(loginData: LoginModel): Promise<FetchResponse<AuthenticateResponse>> {
		// Converte i dati in formato URLEncoded per compatibilità con il backend
		const urlencoded = new URLSearchParams();
		urlencoded.append('Username', loginData.username);
		urlencoded.append('Password', loginData.password);

		return api.post('/api/v1/Authorization/Login', urlencoded, {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		});
	},

	refreshToken(token: string): Promise<FetchResponse<AuthenticateResponse>> {
		// Converte il token in formato URLEncoded per compatibilità con il backend
		const urlencoded = new URLSearchParams();
		urlencoded.append('token', token);

		return api.post('/api/v1/Authorization/RefreshToken', urlencoded, {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		});
	},

	changePassword(passwordData: ChangePasswordModel): Promise<FetchResponse<void>> {
		// Converte i dati in formato URLEncoded per compatibilità con il backend
		const urlencoded = new URLSearchParams();
		urlencoded.append('OldPassword', passwordData.oldPassword);
		urlencoded.append('NewPassword', passwordData.newPassword);

		return api.post('/api/v1/Authorization/ChangePassword', urlencoded, {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		});
	},

	sendResetPassword(username: string): Promise<FetchResponse<void>> {
		// Converte l'username in formato URLEncoded per compatibilità con il backend
		const urlencoded = new URLSearchParams();
		urlencoded.append('username', username);

		return api.post('/api/v1/Authorization/SendResetPassword', urlencoded, {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		});
	},

	resetPassword(resetData: ResetPasswordModel): Promise<FetchResponse<void>> {
		// Converte i dati in formato URLEncoded per compatibilità con il backend
		const urlencoded = new URLSearchParams();
		urlencoded.append('Username', resetData.username);
		urlencoded.append('Token', resetData.token);
		urlencoded.append('NewPassword', resetData.newPassword);

		return api.post('/api/v1/Authorization/ResetPassword', urlencoded, {
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		});
	},

	getUserName(userId: string): Promise<FetchResponse<string>> {
		return api.get(`/api/v1/Users/${userId}/UserName`);
	},

	getUserInfo(): Promise<FetchResponse<ApplicationUserDto>> {
		return api.get('/api/v1/Authorization/UserInfo');
	},

	getImpersonatedUserInfo(workerId: string): Promise<FetchResponse<ApplicationUserDto>> {
		return api.get(`/api/v1/Authorization/ImpersonatedUserInfo/${workerId}`);
	},
};

export default authEndpoints;
